import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { QuantityAndUnitInput } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { PriceAndTaxClassInput } from '@/shared/inputs/PriceAndTaxClassInput.tsx';
import { SelectOrganizationInput } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { NegativePurchasePriceHint, ZeroPurchasePriceHint } from '@/shared/components/PriceInputHints.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { useFormLocations } from '../hooks/useFormLocations.ts';
import { usePositionFormQuantities } from '@/shared/forms/AutomaticProductsForm/hooks/usePositionFormQuantities.ts';

export function PositionPurchaseMaterialFieldset() {
    const methods = useFormContext<AutomaticProductsFormValues>();

    const { t } = useTranslation();
    const { taxClassOptions, defaultValuesLabels, customerOrganization } = useAutomaticFormContext();
    const {
        register,
        watch,
        setValue,
        getValues,
        formState: { defaultValues },
    } = methods;
    const { clearLocations } = useFormLocations(methods);
    const { onPurchaseMaterialUnitChange } = usePositionFormQuantities(methods);

    const [
        purchaseMaterialPrice,
        purchaseMaterialUnit,
        currencyCode,
        showZeroPurchaseMaterialPrice,
        isPurchaseTransportIncluded,
    ] = watch([
        'purchaseMaterialPrice',
        'purchaseMaterialUnit',
        'currencyCode',
        'showZeroPurchaseMaterialPrice',
        'isPurchaseTransportIncluded',
    ]);

    const [supplierOrganizationName, setSupplierOrganizationName] = useState<string | undefined>(
        defaultValuesLabels?.supplierOrganization
    );

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.materialSupplier')}
            dataTest="position-purchase-material-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-2 @2xl:grid-cols-8 grid-cols-1">
                <div className="@2xl:grid @2xl:col-span-5 @2xl:grid-cols-2 @2xl:gap-2 contents">
                    <FieldsetColumn
                        className="@2xl:col-span-1 col-span-full"
                        label={t('forms.automaticProductsForm.labels.supplier')}
                    >
                        <SelectOrganizationInput
                            key={defaultValues?.supplierOrganizationId}
                            {...register('supplierOrganizationId')}
                            defaultValue={defaultValues?.supplierOrganizationId}
                            defaultValueLabel={defaultValuesLabels?.supplierOrganization}
                            placeholder={t('product.addPosition.supplier.label')}
                            exclude={{ ids: [...EXCLUDED_PARTNER_ORGANIZATION_IDS, customerOrganization.id] }}
                            filters={{ organizationType: isPurchaseTransportIncluded ? 'carrier' : 'supplier' }}
                            dataTest="supplier-organization-select-input"
                            onValueChange={org => {
                                const supplierOrganizationId = getValues('supplierOrganizationId');

                                if (org && supplierOrganizationId) {
                                    clearLocations(supplierOrganizationId);
                                }

                                setSupplierOrganizationName(org?.name);
                            }}
                        />
                    </FieldsetColumn>
                    <FieldsetColumn className="col-span-1">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.quantity')}</FieldsetLabel>
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                        </div>
                        <QuantityAndUnitInput
                            quantityProps={{ ...register('purchaseMaterialQuantity'), disabled: true }}
                            unitProps={{
                                ...register('purchaseMaterialUnit'),
                                supportedUnitNames: [...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options],
                                variant: 'lg',
                                onUnitChange: unit => onPurchaseMaterialUnitChange(toAutomaticUnit(unit)),
                            }}
                        />
                    </FieldsetColumn>
                </div>

                <FieldsetColumn className="@2xl:col-span-3 col-span-1">
                    <div className="grid grid-cols-3 gap-1">
                        <FieldsetLabel className="col-span-2">
                            {t('forms.automaticProductsForm.labels.unitPrice')}
                        </FieldsetLabel>
                        <FieldsetLabel className="col-span-1">
                            {t('forms.automaticProductsForm.labels.taxClass')}
                        </FieldsetLabel>
                    </div>
                    <PriceAndTaxClassInput
                        priceProps={{
                            ...register('purchaseMaterialPrice'),
                            unit: purchaseMaterialUnit,
                            currencyCode,
                            testId: 'purchase-material-price-input',
                        }}
                        taxClassProps={{
                            ...register('purchaseMaterialTaxClassId'),
                            options: taxClassOptions,
                            testId: 'purchase-material-tax-class-input',
                        }}
                    />
                </FieldsetColumn>

                {Number(purchaseMaterialPrice) === 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroPurchasePriceHint
                            partnerOrganizationName={supplierOrganizationName}
                            isBillingRelevant={showZeroPurchaseMaterialPrice ?? false}
                            onClick={() =>
                                setValue('showZeroPurchaseMaterialPrice', !(showZeroPurchaseMaterialPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(purchaseMaterialPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativePurchasePriceHint partnerOrganizationName={supplierOrganizationName} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
